import { type FC, useState } from 'react';
import cn from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { useAppSelector } from '../hooks/useReactReduxHooks';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { usePdpSellerDetails } from '../hooks/usePdpSellerDetails';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { PdpRespSidebarDetailsSection } from './PdpRespSidebarDetailsSection';
import { SharedStarRating } from '../../shared/SharedStarRating/SharedStarRating';
import { PdpSharedCallDealerCta } from '../PdpSharedCallDealerCta/PdpSharedCallDealerCta';
import { PdpSharedDealerDetailsFeedback } from '../PdpSharedDealerDetailsFeedback/PdpSharedDealerDetailsFeedback';
import { PdpSharedDealerDetailsBioModalLazy } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBioModalLazy';

// types
import { type PdpRespSidebarDetailsSeller_user$key as User } from './__generated__/PdpRespSidebarDetailsSeller_user.graphql';
import { type PdpRespSidebarDetailsSeller_item$key as Item } from './__generated__/PdpRespSidebarDetailsSeller_item.graphql';

// styles
import dibsCss from 'dibs-css';
import { Link } from 'dibs-elements/src/Link';

const Seller = (
    <FormattedMessage id="PdpRespSidebarDetailsSeller.seller" defaultMessage="Seller Information" />
);

const userFragment = graphql`
    fragment PdpRespSidebarDetailsSeller_user on User {
        ...usePdpSellerDetails_user
        ...PdpSharedCallDealerCta_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespSidebarDetailsSeller_item on Item {
        ...PdpSharedCallDealerCta_item
        seller {
            ...usePdpSellerDetails_seller
            ...SharedStarRating_seller
            ...PdpSharedDealerDetailsFeedback_seller
            ...PdpSharedDealerDetailsBioModalLazy_seller
                @include(if: $afterDisplay)
                @arguments(isExtended: true)
        }
    }
`;

export const PdpRespSidebarDetailsSeller: FC<{ user: User | null; item: Item }> = ({
    user: userRef,
    item: itemRef,
}) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const [isBioModalOpen, setIsBioModalOpen] = useState<boolean>(false);
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});

    const seller = item.seller;

    const { imagePath, name, location, shouldShowBioModal } = usePdpSellerDetails({
        seller,
        user: user || null,
    });

    if (!seller) {
        return null;
    }

    return (
        <PdpRespSidebarDetailsSection title={Seller} ga={{ trigger: 'about_seller' }}>
            <div className={cn(dibsCss.flex, dibsCss.flexCol, dibsCss.gapSmaller)}>
                <div className={cn(dibsCss.flex, dibsCss.gapSmall, dibsCss.itemsCenter)}>
                    {imagePath && (
                        <div className={cn(dibsCss.w60px, dibsCss.h60px)}>
                            <img
                                alt="" // decorative image
                                sizes="(max-width:768px) 60px, 80px, 100px"
                                src={imagePath}
                                srcSet={getSrcsetString(imagePath, [100, 200, 300])}
                                className={cn(
                                    dibsCss.wFull,
                                    dibsCss.hFull,
                                    dibsCss.objectCover,
                                    dibsCss.roundedFull
                                )}
                            />
                        </div>
                    )}
                    <div className={cn(dibsCss.flex, dibsCss.flexCol, dibsCss.sassyFontBodySmall)}>
                        {name && <span className={dibsCss.sassyFontBodySmallHeavy}>{name}</span>}
                        {location && <span>{location}</span>}
                        {shouldShowBioModal && (
                            <>
                                <Link onClick={() => setIsBioModalOpen(true)}>
                                    <FormattedMessage
                                        id="PdpRespSidebarDetailsSeller.viewSellerDetails"
                                        defaultMessage="View Seller Details"
                                    />
                                </Link>
                                {afterDisplay && (
                                    <PdpSharedDealerDetailsBioModalLazy
                                        isExtended
                                        seller={seller}
                                        isOpen={isBioModalOpen}
                                        onClose={() => setIsBioModalOpen(false)}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className={cn(dibsCss.flex, dibsCss.gapSmall)}>
                    <SharedStarRating size={'small'} seller={seller} />
                    <PdpSharedDealerDetailsFeedback seller={seller} isMobile={false} />
                </div>
                <PdpSharedCallDealerCta
                    item={item}
                    user={user}
                    size="small"
                    containerClassName={dibsCss.pt0}
                />
            </div>
        </PdpRespSidebarDetailsSection>
    );
};
